/**
 * Capturing the screen for the Screen Share page.
 *
 * Not `useScreenShare`: that one also drives the meeting's layout and
 * swallows every failure, and this page has no layout and has to tell a
 * dismissed picker from a refusal.
 */
import { isIOSDevice } from '@vc/viewmodels/hooks/useDeviceDetection'

/** Newer fields than the DOM typings know about. */
export type ShareDisplayMediaOptions = DisplayMediaStreamOptions & {
  systemAudio?: 'include' | 'exclude'
  selfBrowserSurface?: 'include' | 'exclude'
}

export function shareCaptureOptions(fps: number): ShareDisplayMediaOptions {
  return {
    // The ceiling the meeting's own share uses (useScreenShare): 1080p, and the
    // chosen frame rate as a cap rather than a target.
    video: {
      frameRate: { ideal: fps, max: fps },
      width: { max: 1920 },
      height: { max: 1080 },
    },
    // A tab's sound only (decision 13). The presenter is usually in the meeting
    // on this same computer, so the whole system's audio would carry everyone's
    // voices back into the room.
    audio: true,
    systemAudio: 'exclude',
    // Not this page itself: a share of the share page shows nothing but itself.
    selfBrowserSurface: 'exclude',
  }
}

export function captureScreen(
  fps: number,
  media: Pick<MediaDevices, 'getDisplayMedia'> = navigator.mediaDevices,
): Promise<MediaStream> {
  return media.getDisplayMedia(shareCaptureOptions(fps))
}

/**
 * The picker was dismissed: nothing to report.
 *
 * Chrome says `NotAllowedError` both for a dismissed picker and for an OS that
 * refuses screen recording ("Permission denied by system", macOS); only the
 * second is worth a message, because only the user's settings can fix it.
 */
export function isCaptureDismissed(err: unknown): boolean {
  const e = err as { name?: unknown; message?: unknown } | null
  if (e?.name === 'AbortError') return true
  if (e?.name !== 'NotAllowedError') return false
  return !/system/i.test(typeof e.message === 'string' ? e.message : '')
}

/**
 * Can this browser share a screen from the Screen Share page?
 *
 * Desktop browsers only for now (decision 15): no mobile browser has
 * `getDisplayMedia`, and iOS — whose iPads report a Mac user agent — is left
 * for later.
 */
export function screenShareOnlySupported(): boolean {
  if (typeof navigator === 'undefined') return false
  if (isIOSDevice()) return false
  return typeof navigator.mediaDevices?.getDisplayMedia === 'function'
}
